import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import cs from 'classnames';

import { shareDocumentWithPractices } from 'api/Documents';

import { getFilestackClient } from 'lib/clients/filestack/filestackClient';
import { ia } from 'lib/helpers/utility';
import { useOrganizationLocations } from 'lib/hooks/queries/organization/useOrganizationLocations';

import useSelectFiles from 'components/practice/comms_center/fax/components/SendFax/components/DocsModal/lib/useSelectFiles';
import { showAlert } from 'components/shared/Alert/Alert';
import Avatar from 'components/shared/Avatar/Avatar';
import Button from 'components/shared/Buttons/Button';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';

const DocumentPracticeShare = ({ selectedFiles, clearSelection, ...rest }) => {
  const { data } = useOrganizationLocations({});

  const [selectedPractices, setSelectedPractices] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const onPracticeSelect = (practice) => {
    setSelectedPractices((prev) =>
      prev.includes(practice.id) ? prev.filter((id) => id !== practice.id) : [...prev, practice.id]
    );
  };

  const { selectFiles, emptyFiles } = useSelectFiles({
    handleInsertFiles: async (files) => {
      if (!ia(files)) return setLoading(false);

      try {
        const filestackClient = await getFilestackClient(navigate);
        const uploadedFiles = await Promise.all(
          files.map(async (file, index) => {
            const response = await filestackClient.upload(file);

            return {
              document_handle: response.handle,
              name: selectedFiles[index].name,
              category: selectedFiles[index].category,
              size: file.size,
              mimetype: file.type,
              id: selectedFiles[index].id,
              patient_name: selectedFiles[index].patient_id
                ? selectedFiles[index].patient?.f_name + ' ' + selectedFiles[index].patient?.l_name
                : null,
              patient_id: selectedFiles[index].patient_id || null
            };
          })
        );

        const data = await shareDocumentWithPractices(navigate, {
          documents: uploadedFiles,
          practiceIds: selectedPractices
        });

        if (data?.code == 0) {
          emptyFiles();
          showAlert({
            title: 'Documents shared successfully',
            color: 'success'
          });
          clearSelection && clearSelection();
          rest.handleClose();
        } else {
          showAlert({
            title: 'Error sharing documents',
            color: 'error'
          });
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        showAlert({
          title: 'Error sharing documents',
          color: 'error'
        });
      } finally {
        setLoading(false);
      }
    },
    selectedFiles
  });

  const handleShare = async () => {
    setLoading(true);
    await selectFiles();
    emptyFiles && emptyFiles();
  };

  return (
    <>
      <Modal
        title="Share document with locations"
        footer={
          <div className="flex gap-2">
            <Button outlined text="Close" onClick={rest.handleClose} color="neutral" />
            <Button
              onClick={handleShare}
              disabled={selectedPractices?.length === 0}
              text="Share"
              type="primary"
              iconColor="white"
              loading={loading}
              icon="send-mono"
            />
          </div>
        }
        {...rest}>
        <div className="flex flex-col gap-2">
          {data?.locations?.map((practice) => (
            <div
              onClick={() => onPracticeSelect(practice)}
              key={practice.id}
              className={cs(
                'flex justify-between gap-2  items-center px-3 py-2 hover:bg-primary-50 cursor-pointer border border-solid border-neutral-100 rounded-lg',
                selectedPractices.includes(practice.id) && 'bg-primary-50'
              )}>
              <div className="flex items-center gap-2">
                <Avatar
                  fullName={practice.name}
                  size="medium"
                  autoBackground={false}
                  fontSize="text-xs"
                />
                <p className="text-primary-900 ">{practice.name}</p>
              </div>
              <div
                className={cs(
                  'flex border border-solid border-neutral-300 w-[20px] h-[20px] rounded-full items-center justify-center',
                  selectedPractices.includes(practice.id) && 'bg-primary-500 border-primary-500'
                )}>
                {selectedPractices.includes(practice.id) && (
                  <Icon icon="check-linear" color="white" size={12} />
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default DocumentPracticeShare;
